import { produce, Immutable } from 'immer';
import { get, set } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import {
  PathSelector,
  Pathable,
  pathSelector as realizePath,
  isSuperPath,
  FormUpdater,
  FormLens,
} from '@tdp/common/form';

/**
 * "Zooms in" on a specific path in the form model, providing a more convenient API for interacting with a section of the model/form.
 * @param modelForm The model fom
 * @param pathSelector a selector for the subpath of the model/form
 * @returns a lens for the interacting with the sub-form/model
 */
export function useZoom<TSchema extends Pathable, TValue>(
  modelForm: FormLens<TSchema>,
  pathSelector: PathSelector<TSchema, TValue>
): FormLens<TValue> {
  const path = useMemo(
    () =>
      Array.isArray(pathSelector) ? pathSelector : realizePath(pathSelector),
    [pathSelector]
  );

  // This is inefficient, but with memoization it is probably suitable for the near-term.
  // if there are any perf issues with render / changes to the validation errors this is the first place to look.
  // TODO(nibeauli): investigate if the debounce/throttle memo hooks are useful here?
  const validationErrors = useMemo(
    () =>
      modelForm.validationErrors.filter((error) =>
        isSuperPath(path, error.path)
      ),
    [modelForm.validationErrors, path]
  );

  // lodash-es has challenges with typing, so we'll just cast this for now.
  // also for some reason it thinks the empty path means undefined and not just the original object.
  const base =
    path.length > 0
      ? get(modelForm.base, path)
      : (modelForm.base as Immutable<TValue>);
  const current =
    path.length > 0
      ? get(modelForm.current, path)
      : (modelForm.current as Immutable<TValue>);

  const updateValue = useCallback(
    (cb: Parameters<FormUpdater<TValue>>[0]) => {
      const next = produce(current, cb);
      modelForm.update((model) => {
        void set(model, path, next);
      });
    },
    [current]
  );

  const clearValue = useCallback(() => {
    if (path.length === 0) {
      //Bugfix for zero step zooms.
      modelForm.clear();
    } else {
      modelForm.update((model) => {
        void set(model, path, base);
      });
    }
  }, [current]);

  return {
    base,
    current,
    update: updateValue,
    clear: clearValue,
    validationErrors,
  };
}
